import { Button, Card, ErrorSummary } from '@beautyburst/ui';
import { semanticColor, space, typeStyle } from '@beautyburst/tokens';
import { customerHref } from '../base-path.ts';
import { useState } from 'react';
import type { FormEvent } from 'react';
import { acceptCustomerInvitation } from '../auth-verification-client.ts';
import { errorsForSummary, FormField, useFormErrors, validatePassword } from '../forms.tsx';

export const ACCEPT_INVITATION_FAILURE =
  'This invitation is invalid, expired, or has already been used. Ask your administrator to send a new invitation.';

export interface AcceptInvitationScreenProps {
  /** Injectable for tests; production uses the real auth transport. */
  acceptInvitation?: (token: string, password: string, fullName?: string) => Promise<unknown>;
}

export function AcceptInvitationScreen({
  acceptInvitation = acceptCustomerInvitation,
}: AcceptInvitationScreenProps = {}) {
  const { errors, setErrors, clear } = useFormErrors();
  const [token] = useState(() => new URLSearchParams(window.location.search).get('token'));
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [pending, setPending] = useState(false);
  const [complete, setComplete] = useState(false);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextErrors: Record<string, string> = {};
    const passwordError = validatePassword(password);
    if (passwordError !== undefined) nextErrors['accept-password-new'] = passwordError;
    if (passwordConfirmation.length === 0) {
      nextErrors['accept-password-confirmation'] = 'Confirm your new password.';
    } else if (passwordConfirmation !== password) {
      nextErrors['accept-password-confirmation'] = 'Passwords must match.';
    }
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }
    if (token === null || token.trim().length === 0) {
      setErrors({ 'accept-password-new': ACCEPT_INVITATION_FAILURE });
      return;
    }

    setPending(true);
    try {
      const trimmedName = fullName.trim();
      await acceptInvitation(token, password, trimmedName.length === 0 ? undefined : trimmedName);
      clear();
      setComplete(true);
      // The token has been consumed; remove it from the URL so it cannot be
      // leaked via history, screenshots, or referrers.
      window.history.replaceState({}, '', customerHref('/accept-invitation'));
    } catch {
      setErrors({ 'accept-password-new': ACCEPT_INVITATION_FAILURE });
    } finally {
      setPending(false);
    }
  };

  return (
    <section
      aria-labelledby="accept-invitation-heading"
      style={{ maxWidth: '32rem', margin: '0 auto', padding: space[6] }}
    >
      <h1
        id="accept-invitation-heading"
        style={{ ...typeStyle('headingLg'), color: semanticColor.textPrimary, margin: 0 }}
      >
        Accept your invitation
      </h1>
      <p
        style={{
          ...typeStyle('body'),
          color: semanticColor.textSecondary,
          margin: `${space[2]} 0 ${space[5]}`,
        }}
      >
        Choose a password to finish setting up your Beauty Burst account.
      </p>
      <Card>
        {complete ? (
          <p
            role="status"
            style={{ ...typeStyle('body'), color: semanticColor.textPrimary, margin: 0 }}
          >
            Your account is ready. Sign in with your email and new password.
          </p>
        ) : (
          <>
            <ErrorSummary errors={errorsForSummary(errors)} />
            <form noValidate onSubmit={submit} style={{ display: 'grid', gap: space[4] }}>
              <FormField
                id="accept-invitation-name"
                label="Full name (optional)"
                error={errors['accept-invitation-name']}
              >
                <input
                  type="text"
                  name="fullName"
                  autoComplete="name"
                  value={fullName}
                  onChange={(event) => {
                    setFullName(event.currentTarget.value);
                    clear('accept-invitation-name');
                  }}
                />
              </FormField>
              <FormField
                id="accept-password-new"
                label="New password"
                hint="Use at least 10 characters, including a letter and a number."
                error={errors['accept-password-new']}
                required
              >
                <input
                  type="password"
                  name="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(event) => {
                    setPassword(event.currentTarget.value);
                    clear('accept-password-new');
                  }}
                />
              </FormField>
              <FormField
                id="accept-password-confirmation"
                label="Confirm new password"
                error={errors['accept-password-confirmation']}
                required
              >
                <input
                  type="password"
                  name="passwordConfirmation"
                  autoComplete="new-password"
                  value={passwordConfirmation}
                  onChange={(event) => {
                    setPasswordConfirmation(event.currentTarget.value);
                    clear('accept-password-confirmation');
                  }}
                />
              </FormField>
              <Button
                type="submit"
                disabled={pending}
                aria-label={pending ? 'Setting up your account…' : 'Set up account'}
              >
                {pending ? 'Setting up your account…' : 'Set up account'}
              </Button>
            </form>
          </>
        )}
        <p
          style={{
            ...typeStyle('bodySm'),
            color: semanticColor.textSecondary,
            margin: `${space[4]} 0 0`,
          }}
        >
          <a href={customerHref('/sign-in')} style={{ color: semanticColor.textLink }}>
            Back to sign in
          </a>
        </p>
      </Card>
    </section>
  );
}
